import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { featuredFilms } from "@/lib/films";

const slides = featuredFilms();

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const goTo = (index: number) => setActive((index + slides.length) % slides.length);
  const current = slides[active];

  return (
    <section
      className="relative w-full overflow-hidden bg-background-100"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点推荐"
    >
      <div className="relative h-[620px] w-full sm:h-[640px] lg:h-[700px]">
        {slides.map((film, index) => (
          <div
            key={film.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={film.backdrop ?? film.poster}
              alt={`《${film.title}》焦点推荐背景图`}
              title={`${film.title} 剧情介绍`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/60" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1440px] items-center px-4 pt-20 md:px-8 md:pt-24 lg:px-12">
          <div className="grid w-full grid-cols-1 items-end gap-8 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="animate-fade">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-500 px-3 py-1 font-label text-[11px] font-medium tracking-wide text-background-50">
                  焦点推荐 · 第 {String(active + 1).padStart(2, "0")} 部
                </span>
                <span className="rounded-full border border-foreground-900/25 px-3 py-1 font-label text-[11px] text-foreground-700">
                  {current.region} · {current.year}
                </span>
                <span className="rounded-full border border-foreground-900/25 px-3 py-1 font-label text-[11px] text-foreground-700">
                  {current.genres.join(" / ")}
                </span>
              </div>

              <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 sm:text-4xl lg:text-[52px] lg:leading-[1.12]">
                {current.title}
              </h2>
              <p className="mt-2 font-label text-xs uppercase tracking-[0.32em] text-foreground-500">
                {current.enTitle}
              </p>

              <p className="mt-5 max-w-xl font-body text-sm leading-relaxed text-foreground-700 md:text-base">
                {current.tagline}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-star-line text-accent-500" />
                  导演 {current.director}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-accent-500" />
                  {current.duration} 分钟
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-group-line text-accent-500" />
                  主演 {current.cast.slice(0, 2).join("、")}
                </span>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 font-label text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-list-line text-base" />
                  查看剧情介绍
                </Link>
                <Link
                  to="/category"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-foreground-900/25 px-6 font-label text-sm text-foreground-800 backdrop-blur-sm transition-colors hover:border-accent-500 hover:text-accent-500"
                >
                  浏览全部分类
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            <div className="hidden justify-end lg:flex">
              <div className="flex flex-col gap-2">
                {slides.map((film, index) => (
                  <button
                    key={film.id}
                    type="button"
                    onClick={() => goTo(index)}
                    className={`group flex w-[300px] items-center gap-3 rounded-lg border p-2 text-left transition-colors ${
                      index === active
                        ? "border-accent-500/70 bg-background-100/85"
                        : "border-background-300/60 bg-background-50/40 hover:border-foreground-500/40"
                    }`}
                    aria-label={`切换到《${film.title}》`}
                  >
                    <span className="relative h-14 w-10 shrink-0 overflow-hidden rounded">
                      <img
                        src={film.poster}
                        alt={`《${film.title}》缩略海报`}
                        title={`${film.title} 海报`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-heading text-sm font-semibold text-foreground-900">
                        {film.title}
                      </span>
                      <span className="mt-0.5 block truncate font-label text-[11px] text-foreground-500">
                        {film.year} · {film.director}
                      </span>
                    </span>
                    <i
                      className={`ri-arrow-right-s-line text-base ${
                        index === active ? "text-accent-500" : "text-foreground-500"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-6 z-10 mx-auto flex w-full max-w-[1440px] items-center justify-between px-4 md:px-8 lg:px-12">
          <div className="flex items-center gap-2">
            {slides.map((film, index) => (
              <button
                key={film.id}
                type="button"
                onClick={() => goTo(index)}
                aria-label={`第 ${index + 1} 张焦点图`}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === active ? "w-8 bg-accent-500" : "w-4 bg-foreground-600/50 hover:bg-foreground-500"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(active - 1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-900/25 text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-500"
              aria-label="上一部"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => goTo(active + 1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground-900/25 text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-500"
              aria-label="下一部"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}